import {
  AntDesign,
  Ionicons,
  MaterialCommunityIcons,
} from "@expo/vector-icons";
import { BlurView } from "expo-blur";
import { router } from "expo-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { View, Modal, Pressable, Text } from "react-native";
import { useDispatch } from "react-redux";

import ButtonSmall from "@/components/button/ButtonSmall";
import { setForm } from "@/context/actions/actions";
import { truncateMiddle } from "@/utils/text/FormatTextUtils";

interface BeneficiaryModalProps {
  visible: boolean;
  onClick?: (state: boolean) => void;
  onClose: () => void;
  onAction?: () => void;
  data: any;
}

export default function BeneficiaryModal(props: BeneficiaryModalProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const profileKeysToIgnore = ["relationshipToCustomer", "address"];
  const accountKeysToIgnore = ["assetTypeId", "institutionAddress"];
  const walletKeysToIgnore = ["walletId", "institutionAddress"];

  const renderItems = (data: any, keysToIgnore: string[]) => {
    return Object.keys(data).map((key) => {
      if (keysToIgnore.includes(key)) return null;

      if (typeof data[key] === "object") {
        return (
          <View key={key} className="w-full items-center">
            <Text className="text-sm font-thin">{key}:</Text>
            {renderItems(data[key], [])}
          </View>
        );
      }

      return (
        <View
          key={key}
          className="w-full flex-row justify-between items-center mb-2"
        >
          <Text className="text-sm font-thin">
            {t(`home/contacts:details.${key}`)}:
          </Text>
          <Text className="text-sm">{truncateMiddle(data[key], 30)}</Text>
        </View>
      );
    });
  };

  const renderAddress = (address: any) => {
    return (
      <View className="w-full items-center">
        {address.addressLine1 && address.city && address.state && (
          <View className="w-full flex-row justify-between items-center mb-2">
            <Text className="text-sm font-thin">
              {t("home/contacts:details.address")}:
            </Text>
            <Text className="text-sm">{`${address.addressLine1}, ${address.city} - ${address.state}`}</Text>
          </View>
        )}
        {address.postalCode && (
          <View className="w-full flex-row justify-between items-center mb-2">
            <Text className="text-sm font-thin">
              {t("home/contacts:details.postalCode")}:
            </Text>
            <Text className="text-sm">{address.postalCode ?? ""}</Text>
          </View>
        )}
        {address.countryCode && (
          <View className="w-full flex-row justify-between items-center mb-2">
            <Text className="text-sm font-thin">
              {t("home/contacts:details.countryCode")}:
            </Text>
            <Text className="text-sm">{address.countryCode ?? ""}</Text>
          </View>
        )}
      </View>
    );
  };

  const handlePress = () => {
    dispatch(setForm({ currentCounterpartySelected: props.data }));
    dispatch(setForm({ rail: props.data.supportedRails[0] || "" }));

    if (
      !props.data.accountInformation?.accountNumber &&
      props.data.accountInformation?.iban
    ) {
      dispatch(setForm({ isIbanAccount: true }));
      dispatch(setForm({ isClabeAccount: false }));
    } else if (
      !props.data.accountInformation?.iban &&
      props.data.accountInformation?.accountNumber
    ) {
      dispatch(setForm({ isIbanAccount: false }));
      dispatch(setForm({ isClabeAccount: false }));
    } else if (props.data?.accountInformation?.clabe) {
      dispatch(setForm({ isClabeAccount: true }));
      dispatch(setForm({ isIbanAccount: false }));
    }
    props.onClose();
    router.push("/home/transaction/amount");
  };

  return (
    <Modal visible={props.visible} transparent animationType="fade">
      <BlurView
        intensity={20}
        className="z-50 flex-1 justify-center items-center"
      >
        <View className="p-6 bg-white1 border border-gray rounded-lg w-full h-full items-center justify-center lg:w-104 lg:h-152 shadow">
          <Pressable
            className="z-50 absolute right-4 top-4"
            onPress={() => props.onClose()}
          >
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
          <Pressable
            className="z-50 absolute left-4 top-4"
            onPress={() => {
              props.onClose();
              props.onAction();
            }}
            onHoverIn={() => setIsHovered(true)}
            onHoverOut={() => setIsHovered(false)}
          >
            <Ionicons
              name="trash-outline"
              size={24}
              color={isHovered ? "red" : "black"}
            />
          </Pressable>
          <View className="flex-grow w-full items-center">
            <View className="mb-4">
              <Text className="text-2xl text-center text-blue1 font-medium mb-3">
                {t("home/contacts:modal.title")}
              </Text>
            </View>
            <View className="w-full items-center">
              <View className="rounded-full border border-black1 p-2 mb-2">
                <Ionicons
                  name="person-outline"
                  size={20}
                  className="text-black1"
                />
              </View>
              {renderItems(props.data?.profile, profileKeysToIgnore)}
              {renderAddress(props.data?.profile?.address)}
              {props.data.description && (
                <View className="w-full flex-row justify-between items-center mb-2">
                  <Text className="text-sm font-thin">
                    {t("home/contacts:details.additionalInformation")}:
                  </Text>
                  <Text className="text-sm">
                    {truncateMiddle(props.data.description, 30)}
                  </Text>
                </View>
              )}
            </View>
            <View className="w-full items-center">
              <View className="rounded-full border border-black1 p-2 mb-2">
                <MaterialCommunityIcons
                  name="bank-outline"
                  size={20}
                  className="text-black1"
                />
              </View>
              {props.data?.accountInformation && (
                <>
                  {renderItems(
                    props.data?.accountInformation,
                    accountKeysToIgnore
                  )}
                </>
              )}
              {props.data?.walletInformation && (
                <>
                  {renderItems(
                    props.data?.walletInformation,
                    walletKeysToIgnore
                  )}
                </>
              )}
            </View>
            <View className="flex-grow justify-end items-center">
              <ButtonSmall
                label={t("common:button.selectbeneficiary")}
                onClick={handlePress}
                disabled={props.data.status !== "ACTIVE"}
              />
            </View>
          </View>
        </View>
      </BlurView>
    </Modal>
  );
}
